/**
 * ChannelVersionHistoryModal Component
 *
 * Shows complete version history for a channel with:
 * - Accordion list of all versions
 * - All slots side-by-side when expanded
 * - Rollback options (complete or single slot)
 */
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/v2/ui/Dialog';
import { Button, Badge, Loading } from '@/components/v2/ui';
import { ChevronDown, ChevronRight, RotateCcw, Clock, User, ChevronLeft, ChevronRight as ChevronRightIcon } from 'lucide-react';
import { cn, formatDateTime } from '@/lib/utils';
import { getChannelHistory, rollbackChannelVersion, rollbackChannelSlot } from '@/lib/api/channel-history';

interface ChannelVersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  channelId: string;
  channelName: string;
}

export function ChannelVersionHistoryModal({
  isOpen,
  onClose,
  productId,
  channelId,
  channelName,
}: ChannelVersionHistoryModalProps) {
  const { t } = useTranslation();
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());
  const [rollingBack, setRollingBack] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch channel version history
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['channel-history', productId, channelId],
    queryFn: () => getChannelHistory(productId, channelId),
    enabled: isOpen,
  });

  const versions = useMemo(() => data?.versions || [], [data?.versions]);

  // Auto-expand first (published) version
  useEffect(() => {
    if (versions.length > 0 && isOpen) {
      const firstVersion = versions[0];
      setExpandedVersions(new Set([firstVersion.version_id]));
      setCurrentPage(1); // Reset to first page when modal opens
    }
  }, [versions, isOpen]);

  // Pagination
  const totalPages = Math.ceil(versions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedVersions = versions.slice(startIndex, endIndex);

  const toggleVersion = (versionId: string) => {
    setExpandedVersions((prev) => {
      const next = new Set(prev);
      if (next.has(versionId)) {
        next.delete(versionId);
      } else {
        next.add(versionId);
      }
      return next;
    });
  };

  const handleRollbackComplete = async (versionId: string) => {
    setRollingBack(versionId);
    try {
      const result = await rollbackChannelVersion(productId, channelId, versionId);
      if (result.success) {
        toast.success(result.message || t('toast.success.channelReset'));
        refetch();
        onClose();
      } else {
        toast.error(result.error || t('toast.error.rollback'));
      }
    } catch (error) {
      console.error('Rollback failed:', error);
      toast.error(t('toast.error.channelReset'));
    } finally {
      setRollingBack(null);
    }
  };

  const handleRollbackSlot = async (versionId: string, slot: number) => {
    setRollingBack(`${versionId}-slot-${slot}`);
    try {
      const result = await rollbackChannelSlot(productId, channelId, versionId, slot);
      if (result.success) {
        toast.success(result.message || t('toast.success.slotReset', { slot }));
        refetch();
      } else {
        toast.error(result.error || t('toast.error.rollback'));
      }
    } catch (error) {
      console.error('Rollback slot failed:', error);
      toast.error(t('toast.error.slotReset', { slot }));
    } finally {
      setRollingBack(null);
    }
  };

  const getSourceTypeLabel = (sourceType: string) => {
    switch (sourceType) {
      case 'user_edit':
        return 'Manuell bearbeitet';
      case 'rollback':
        return 'Rollback';
      case 'manual_publish':
        return 'Manuell publiziert';
      case 'regenerate':
        return 'Regeneriert';
      default:
        return sourceType;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            Versionshistorie: {channelName}
          </DialogTitle>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Alle gespeicherten Versionen dieses Channels
          </p>
        </DialogHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto pr-2">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loading size="lg" label={t('common.loading')} />
            </div>
          )}

          {!isLoading && versions.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-neutral-500 dark:text-neutral-400">
              <Clock className="h-12 w-12 mb-4" />
              <p className="text-sm">Keine Versionshistorie vorhanden</p>
            </div>
          )}

          {!isLoading && versions.length > 0 && (
            <>
              <div className="space-y-3">
                {paginatedVersions.map((version) => {
                const isExpanded = expandedVersions.has(version.version_id);
                const isRollingBack = rollingBack === version.version_id;

                return (
                  <div
                    key={version.version_id}
                    className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden"
                  >
                    {/* Accordion Header */}
                    <div
                      className={cn(
                        'w-full flex items-center gap-3 p-4 transition-colors',
                        'hover:bg-neutral-50 dark:hover:bg-neutral-800',
                        isExpanded && 'bg-neutral-50 dark:bg-neutral-800'
                      )}
                    >
                      {/* Expand Icon - Clickable */}
                      <button
                        onClick={() => toggleVersion(version.version_id)}
                        className="flex-shrink-0 p-1 -m-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-neutral-500" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-neutral-500" />
                        )}
                      </button>

                      {/* Version Info - Clickable */}
                      <div
                        onClick={() => toggleVersion(version.version_id)}
                        className="flex-1 min-w-0 cursor-pointer"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-neutral-900 dark:text-neutral-50">
                            {version.status === 'published'
                              ? `Aktuelle Version (V${version.version_number})`
                              : `Version #${version.version_number}`
                            }
                          </span>
                          <Badge variant={getStatusBadgeVariant(version.status)}>
                            {version.status}
                          </Badge>
                          <span className="text-xs text-neutral-500">
                            {getSourceTypeLabel(version.source_type)}
                          </span>
                        </div>

                        <div className="flex items-center gap-4 text-xs text-neutral-600 dark:text-neutral-400">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDateTime(version.created_at)}
                          </span>
                          {version.changed_by && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {version.changed_by}
                            </span>
                          )}
                          <span className="text-neutral-500">
                            {version.slots.length} Slot{version.slots.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>

                      {/* Rollback Complete Button - Independent */}
                      {version.status !== 'published' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRollbackComplete(version.version_id)}
                          disabled={isRollingBack}
                          className="flex-shrink-0"
                        >
                          {isRollingBack ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-2" />
                              Wiederherstelle...
                            </>
                          ) : (
                            <>
                              <RotateCcw className="h-3 w-3 mr-2" />
                              Alle wiederherstellen
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    {/* Accordion Content - Slots Grid */}
                    {isExpanded && (
                      <div className="p-4 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                          {version.slots.map((slot) => {
                            const isRollingBackSlot = rollingBack === `${version.version_id}-slot-${slot.slot}`;

                            return (
                              <div
                                key={`${version.version_id}-${slot.link_id}`}
                                className="group relative w-[180px] h-[180px] bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden"
                              >
                                {/* Image */}
                                <img
                                  src={slot.asset_id ? `/api/v1/asset/${slot.asset_id}/image` : '/images/placeholder-image.png'}
                                  alt={slot.alt_text || `Slot ${slot.slot}`}
                                  className="w-full h-full object-cover"
                                />

                                {/* Slot Badge */}
                                <Badge
                                  variant="primary"
                                  className="absolute top-2 left-2"
                                >
                                  Slot #{slot.slot}
                                </Badge>

                                {/* Dimensions */}
                                {slot.width && slot.height && (
                                  <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                                    {slot.width} × {slot.height}
                                  </div>
                                )}

                                {/* Rollback Single Slot Button */}
                                {version.status !== 'published' && (
                                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button
                                      size="sm"
                                      variant="primary"
                                      onClick={() => handleRollbackSlot(version.version_id, slot.slot)}
                                      disabled={isRollingBackSlot}
                                    >
                                      {isRollingBackSlot ? (
                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                                      ) : (
                                        <>
                                          <RotateCcw className="h-3 w-3 mr-2" />
                                          {t('common.restore')}
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Alt Text if present */}
                        {version.slots.some((s) => s.alt_text) && (
                          <div className="mt-4 space-y-2">
                            {version.slots
                              .filter((s) => s.alt_text)
                              .map((s) => (
                                <div
                                  key={s.slot}
                                  className="text-xs text-neutral-600 dark:text-neutral-400"
                                >
                                  <span className="font-semibold">Slot #{s.slot}:</span>{' '}
                                  {s.alt_text}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    Seite {currentPage} von {totalPages}
                  </span>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <Button variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
